import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

// Supabase reports a failed OAuth round-trip by redirecting back to the app
// with error details attached — as query params for the PKCE flow, or in the
// hash fragment for the implicit flow. Without this the user silently lands on
// a page with no idea why they are still signed out.
const readError = (search: string, hash: string) => {
  for (const raw of [search, hash.replace(/^#/, "?")]) {
    const params = new URLSearchParams(raw);
    const error = params.get("error") || params.get("error_code");
    if (error) {
      return params.get("error_description")?.replace(/\+/g, " ") || error;
    }
  }
  return null;
};

const OAuthErrorHandler = () => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // An expired or already-used password-reset link lands on /reset-password
    // with the same error parameters. That page explains it and offers a new
    // link, so a "Sign-in failed" toast on top would only confuse.
    if (pathname === "/reset-password") return;
    const message = readError(search, hash);
    if (!message) return;
    toast.error(`Sign-in failed: ${message}`);
    // Strip the error off the URL so a refresh doesn't re-toast it.
    navigate(pathname, { replace: true });
  }, [pathname, search, hash, navigate]);

  return null;
};

export default OAuthErrorHandler;
